import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { printRegistryDoc } from '../../print/registryDocPrint';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Button, Card, ConfirmDialog, DataGrid, Spinner, Spreadsheet, useExport, useToast,
  type DataGridColumn,
} from '@kss/ui';
import { apiClient, apiResources, apiResourcesByGroup, type ApiResourceMeta } from '@kss/api';
import {
  BarChart3, ChevronDown, ChevronLeft, Download, Grid3X3, LayoutGrid, Pencil, Plus, Printer,
  RefreshCw, Save, Search, Share2, Table2, Trash2, Upload, X, ZoomIn, FileDown, UserCog,
} from 'lucide-react';
import { GenericCrudForm, isActiveFromStatus } from './GenericCrudForm';
import { invalidateFkCache, lookupFkTarget, prefetchFks, resolveFkLabel, getFkOptions, type FkOption } from './fkResolver';
import { KIND_SLUG } from '../transactions/TransactionEntryShell';
import AnalyticsDashboard from './AnalyticsDashboard';
import { AuthImg } from '../../components/AuthImg';

/**
 * Auto-generated registry-driven browser for every entity exposed by the
 * live KSS API. The page works for ALL 280+ resources without needing a
 * dedicated component per entity. Routes:
 *
 *   /panel/api/                      — group / resource index
 *   /panel/api/:group                — list of resources in one group
 *   /panel/api/:group/:resource      — live grid of that resource
 */

/**
 * Order rows newest-first. The backend returns lists ASC (oldest id first);
 * users expect the latest entry on top. Primary key is the numeric id
 * (autoincrement → highest = newest); falls back to createdAt/indate/updatedAt
 * timestamps when id isn't numeric.
 */
function sortNewestFirst(list: Record<string, unknown>[]): Record<string, unknown>[] {
  const rank = (r: Record<string, unknown>): number => {
    const id = Number(r.id);
    if (Number.isFinite(id)) return id;
    const d = r.createdAt ?? r.indate ?? r.updatedAt ?? r.createdOn;
    const t = d ? Date.parse(String(d)) : NaN;
    return Number.isFinite(t) ? t : 0;
  };
  return [...list].sort((a, b) => rank(b) - rank(a));
}

export default function ApiBrowser() {
  const { group, resource } = useParams<{ group?: string; resource?: string }>();

  if (group && resource) {
    return <ResourceGrid group={group} resource={resource} />;
  }
  if (group) {
    return <GroupView group={group} />;
  }
  return <IndexView />;
}

function IndexView() {
  return (
    <div className="-m-6 space-y-3 p-[3px]">
      <div className="rounded-xl border border-border bg-surface-card px-4 py-3">
        <h1 className="text-base font-semibold text-content-primary">API modules</h1>
        <p className="text-xs text-content-muted">
          Auto-generated from the live KSS swagger spec — {apiResources.length} resources across{' '}
          {Object.keys(apiResourcesByGroup).length} groups.
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {Object.keys(apiResourcesByGroup).sort().map((g) => (
          <Link
            key={g}
            to={`/panel/api/${g}`}
            className="rounded-lg border border-border bg-surface-card p-4 transition-colors hover:border-primary"
          >
            <div className="font-medium text-content-primary">{g}</div>
            <div className="mt-0.5 text-xs text-content-muted">
              {apiResourcesByGroup[g].length} resource(s)
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

/**
 * Per-module landing view. Earlier this was a flat list of resources
 * with a tiny "Analytics" link in the corner — users never noticed it
 * and complained "module-wise dashboard not showing". Now the module
 * URL renders the AnalyticsDashboard at the top (KPIs + recent-row
 * counts + activity charts) and the resource list collapses below
 * under "Browse resources".
 */
function GroupView({ group }: { group: string }) {
  const list = apiResourcesByGroup[group] ?? [];
  const [showResources, setShowResources] = useState(false);
  return (
    <div className="-m-6 space-y-3 p-[3px]">
      {/* AnalyticsDashboard reads `group` from the route; rendering it
          here means /panel/api/<group> shows the dashboard by default. */}
      <AnalyticsDashboard />
      <div className="rounded-xl border border-border bg-surface-card">
        <button
          type="button"
          onClick={() => setShowResources((v) => !v)}
          className="flex w-full items-center gap-3 px-4 py-3 text-left"
        >
          <ChevronDown
            size={16}
            className={`text-content-muted transition-transform ${showResources ? 'rotate-180' : ''}`}
          />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-content-primary">
              Browse resources ({list.length})
            </div>
            <div className="text-[11px] text-content-muted">
              Direct access to every entity in this module
            </div>
          </div>
        </button>
        {showResources && (
          <div className="grid gap-2 border-t border-border p-3 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((r) => (
              <Link
                key={r.name}
                to={`/panel/api/${group}/${r.name}`}
                className="rounded-md border border-border bg-surface-card px-3 py-2 text-sm transition-colors hover:border-primary"
              >
                <div className="font-medium text-content-primary">{r.name}</div>
                <div className="text-[11px] text-content-muted">/api{r.basePath}</div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ResourceGrid({ group, resource }: { group: string; resource: string }) {
  const meta = useMemo<ApiResourceMeta | undefined>(
    () => apiResources.find((r) => r.group === group && r.name === resource),
    [group, resource],
  );
  const navigate = useNavigate();

  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<Record<string, unknown> | null | undefined>(undefined);
  const [confirmDelete, setConfirmDelete] = useState<Record<string, unknown> | null>(null);
  const [deleting, setDeleting] = useState(false);
  /** Bumped after `prefetchFks` resolves so column render functions pick up names. */
  const [fkVersion, setFkVersion] = useState(0);
  /**
   * 'grid' = read-only DataGrid (default).
   * 'sheet' = react-spreadsheet bulk-edit mode. Cell edits collect in
   *           `sheetDraft`; clicking Save PUTs every dirty row in turn.
   */
  const [viewMode, setViewMode] = useState<'grid' | 'sheet'>('grid');
  const [sheetDraft, setSheetDraft] = useState<Record<string, unknown>[] | null>(null);
  const [sheetDirty, setSheetDirty] = useState<Set<number>>(new Set());
  const [sheetSaving, setSheetSaving] = useState(false);
  const toast = useToast();
  const { exportToExcel } = useExport();

  // ─── new toolbar state ───────────────────────────────────────
  // Search input collapses to an icon by default; click expands it
  // to a textbox with a close (X) chip.
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // Category filter — for resources with a *category* FK column (e.g.
  // MenuItem.menuCategoryId, Product.categoryId). Renders a dropdown that
  // filters the grid by the chosen category.
  // Multi-select category filter (checkbox dropdown): the set of selected
  // category ids; empty = show all.
  const [categoryFilter, setCategoryFilter] = useState<Set<string>>(new Set());
  const [categoryOpts, setCategoryOpts] = useState<FkOption[]>([]);
  // Row selection — header checkbox = all-on-this-page, individual
  // checkboxes per row. Used by future bulk actions.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Image lightbox — click a thumbnail in the grid to preview full.
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  // Hidden file input for Import data.
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const [importing, setImporting] = useState(false);

  async function load() {
    if (!meta) return;
    setLoading(true);
    setErr(null);
    try {
      const r = await apiClient.get(meta.basePath);
      const data: unknown = r.data;
      let list: Record<string, unknown>[] = [];
      if (Array.isArray(data)) list = data as Record<string, unknown>[];
      else if (data && typeof data === 'object') {
        const obj = data as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } };
        if (Array.isArray(obj.items)) list = obj.items as Record<string, unknown>[];
        else if (Array.isArray(obj.data)) list = obj.data as Record<string, unknown>[];
        else if (obj.data && Array.isArray((obj.data as { items?: unknown[] }).items)) {
          list = (obj.data as { items: unknown[] }).items as Record<string, unknown>[];
        }
      }
      // The API returns rows oldest-first (ASC by id). Default the grid to
      // newest-first so the latest entry is on top — users still re-sort by
      // clicking any column header.
      setRows(sortNewestFirst(list));
      // Resolve FK columns (departmentId, designationId, …) to names by
      // pre-fetching the target entities once. The columns useMemo
      // re-runs when `fkVersion` bumps, which lets the labels render
      // on the next paint without a full reload.
      void prefetchFks(list, { selfResource: meta }).then(() => {
        setFkVersion((v) => v + 1);
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group, resource]);

  // Inline workflow-status change (e.g. a restaurant order's orderStatus
  // Placed→Cooking→Ready→Delivered). PUTs the row with the new value and
  // optimistically updates the grid, so admins can advance status without
  // opening the editor.
  const [savingStatus, setSavingStatus] = useState<string | null>(null);
  async function updateRowStatus(row: Record<string, unknown>, field: string, value: string) {
    if (!meta) return;
    const id = row.id ?? row[meta.idField];
    const key = String(id ?? row.code ?? '');
    setSavingStatus(`${key}:${field}`);
    setRows((cur) => cur.map((r) => (r === row ? { ...r, [field]: value } : r)));
    try {
      // Spread the FULL existing row — the backend PUT is a full-record
      // REPLACE, so sending only {id, code, status, field} nulls every other
      // column (customerName, grandTotal, billingAddress, orderNumber, the
      // line-JSON in description …). On SalesOrder that even blanked `code`
      // (the grid's rowId) → duplicate keys → "table won't load". Keeping the
      // whole row preserves the document; we only override the one field.
      //
      // BUT drop the server-managed audit/system columns: echoing a STALE
      // `updatedAt` can trip optimistic-concurrency on the backend (the PUT is
      // accepted client-side but rejected server-side → the status silently
      // reverted to its old value, e.g. an order cancel that "didn't stick").
      const { updatedAt, indate, createdBy, updatedBy, deletedAt, deletedBy, ...clean } = row as Record<string, unknown>;
      void updatedAt; void indate; void createdBy; void updatedBy; void deletedAt; void deletedBy;
      await apiClient.put(meta.basePath, {
        ...clean,
        ...(id != null ? { id: Number(id) } : {}),
        status: typeof row.status === 'number' ? row.status : 0,
        [field]: value,
      });
      toast.success('Status updated', `${humaniseHeader(field)} → ${value}`);
    } catch (e) {
      // Surface WHY it failed instead of silently snapping back — a backend
      // rejection (invalid transition, validation) was previously invisible.
      toast.error('Status not saved', e instanceof Error ? e.message : 'The server rejected the change.');
      void load(); // revert to the server's authoritative value
    } finally {
      setSavingStatus(null);
    }
  }

  const columns = useMemo<DataGridColumn<Record<string, unknown>>[]>(() => {
    if (!meta) return [];
    if (rows.length === 0) {
      return [{ accessor: meta.idField, title: meta.idField }];
    }
    const seen = new Set<string>();
    for (const r of rows.slice(0, 25)) {
      for (const k of Object.keys(r)) seen.add(k);
    }
    // Hide noise columns from the registry-driven grid. Tenant id is
    // implicit (all rows are scoped to the current tenant), and the
    // audit columns (created/updated/deleted) just push the meaningful
    // business fields off-screen.
    let filtered = [...seen].filter((k) => !HIDDEN_GRID_COLUMNS.has(k));
    // De-duplicate the generic `code` column when the resource ALSO carries a
    // domain-specific human number (orderNumber, invoiceNumber, challanNumber…)
    // that holds the SAME value. Many docs set code = the number, so the grid
    // would otherwise render two identical columns — reported on Sales Orders
    // ("order no" and "code" are two same columns). We only drop `code` when a
    // sibling *Number/*No column genuinely matches it across the sampled rows,
    // so resources where `code` is the sole identifier keep it.
    if (filtered.includes('code')) {
      const sample = rows.slice(0, 25);
      const numberKeys = filtered.filter((k) => k !== 'code' && /(?:number|no)$/i.test(k));
      // Compare only rows where BOTH code and the candidate number are filled —
      // otherwise seed rows like SAMPLE-SalesOrder (code set, orderNumber blank)
      // would defeat the check. If every such pair matches, `code` is redundant.
      const redundant = numberKeys.some((nk) => {
        const pairs = sample.filter((r) =>
          String(r.code ?? '').trim() !== '' && String(r[nk] ?? '').trim() !== '');
        return pairs.length > 0 && pairs.every((r) =>
          String(r.code ?? '').trim() === String(r[nk] ?? '').trim());
      });
      if (redundant) filtered = filtered.filter((k) => k !== 'code');
    }
    // First column = select-all checkbox + per-row checkboxes.
    const selectionCol: DataGridColumn<Record<string, unknown>> = {
      accessor: '__select' as never,
      title: (
        <input
          type="checkbox"
          className="h-4 w-4 cursor-pointer accent-primary"
          // Header checkbox state mirrors whether ALL visible rows are
          // currently in selectedIds.
          checked={
            rows.length > 0 &&
            rows.every((r) => selectedIds.has(rowKey(r)))
          }
          ref={(el) => {
            // Indeterminate when some-but-not-all are selected.
            if (el) {
              const someSelected = rows.some((r) => selectedIds.has(rowKey(r)));
              const allSelected = rows.length > 0 && rows.every((r) => selectedIds.has(rowKey(r)));
              el.indeterminate = someSelected && !allSelected;
            }
          }}
          onChange={(e) => {
            const next = new Set(selectedIds);
            if (e.target.checked) {
              for (const r of rows) next.add(rowKey(r));
            } else {
              for (const r of rows) next.delete(rowKey(r));
            }
            setSelectedIds(next);
          }}
          aria-label="Select all rows"
        /> as unknown as string),
      width: 48,
      render: (row: Record<string, unknown>) => {
        const k = rowKey(row);
        return (
          <input
            type="checkbox"
            className="h-4 w-4 cursor-pointer accent-primary"
            checked={selectedIds.has(k)}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              const next = new Set(selectedIds);
              if (e.target.checked) next.add(k);
              else next.delete(k);
              setSelectedIds(next);
            }}
            aria-label="Select row"
          />
        );
      },
    };

    // Show the first 11 business columns — but always include any image
    // column (imageUrl/logoUrl/…) even if it sorts past the cap, so e.g.
    // ProductVariant.imageUrl (14th field) still renders a thumbnail.
    const isImageKey = (k: string) => /image|photo|avatar|logo|picture|thumbnail/i.test(k);
    const headKeys = filtered.slice(0, 11);
    const imageExtras = filtered.filter((k) => isImageKey(k) && !headKeys.includes(k));
    // Always surface key money / status columns (grandTotal, paymentStatus, …)
    // even if they sort past the cap — otherwise wide schemas hide them.
    const importantExtras = filtered.filter((k) => IMPORTANT_GRID_COLUMNS.has(k.toLowerCase()) && !headKeys.includes(k) && !imageExtras.includes(k));
    const dataCols: DataGridColumn<Record<string, unknown>>[] = [...headKeys, ...imageExtras, ...importantExtras].map((k) => {
      const fkTarget = lookupFkTarget(k, { selfResource: meta });
      const isImageField = /image|photo|avatar|logo|picture|thumbnail/i.test(k);
      return {
        accessor: k,
        // Friendlier title — strip the trailing "Id" for FK columns so
        // grid headers read "Department" instead of "departmentId".
        title: fkTarget ? humaniseHeader(k.replace(/Id$/, '')) : humaniseHeader(k),
        sortable: true,
        filterable: true,
        render: (row: Record<string, unknown>) => {
          // 1) isActive (and similar 0/1 booleans) → "Active" / "Inactive" pill.
          if (k === 'isActive' || k === 'isAvailable' || k === 'isBlacklisted' || k === 'isVeg' || k === 'isPublished') {
            const truthy = isTruthy(row[k]);
            return <StatusPill on={truthy} label={truthy ? 'Active' : 'Inactive'} />;
          }
          // 1a) `status` is the numeric §35 enum (0=Active, 1=Passive,
          //     2=Deleted). Render the friendly label, not a bare "0".
          if (k === 'status' && (typeof row[k] === 'number' || /^\d+$/.test(String(row[k] ?? '')))) {
            const n = isActiveFromStatus(row[k]);
            const label = n === 1 ? 'Passive' : n === 2 ? 'Deleted' : 'Active';
            return <StatusPill on={n === 0} label={label} />;
          }
          // 1b) Image columns render as a clickable thumbnail. The stored
          //     value is usually a RELATIVE path (e.g. /api/Image/<hash>.jpeg),
          //     not an absolute http URL — resolveImageSrc + AuthImg handle
          //     that, so render for any non-empty string value.
          if (isImageField && typeof row[k] === 'string' && (row[k] as string).trim() !== '') {
            return <ImageThumbnail src={row[k] as string} onOpen={() => setImagePreview(row[k] as string)} />;
          }
          // 1c) `description` holding the transaction-shell JSON envelope renders
          //     as a readable item summary instead of raw JSON.
          if (k === 'description' && typeof row[k] === 'string' && (row[k] as string).trim().startsWith('{')) {
            try {
              const meta = JSON.parse(row[k] as string) as { lines?: Array<{ name?: unknown }> };
              if (Array.isArray(meta.lines) && meta.lines.length) {
                const names = meta.lines.map((l) => String(l.name ?? '')).filter(Boolean);
                return (
                  <span className="text-content-secondary" title={names.join(', ')}>
                    {meta.lines.length} item{meta.lines.length > 1 ? 's' : ''}: {names.slice(0, 3).join(', ')}{names.length > 3 ? '…' : ''}
                  </span>
                );
              }
            } catch { /* not the envelope — fall through */ }
          }
          // 1d) Workflow-status columns (orderStatus, challanStatus, …) render
          //     as an inline dropdown so an admin can advance the stage.
          if (WORKFLOW_STATUS_OPTIONS[k]) {
            const opts = WORKFLOW_STATUS_OPTIONS[k];
            const cur = String(row[k] ?? '');
            const rid2 = String(row.id ?? row.code ?? '');
            const busyStatus = savingStatus === `${rid2}:${k}`;
            const choices = cur && !opts.includes(cur) ? [cur, ...opts] : opts;
            return (
              <select
                value={cur}
                disabled={busyStatus}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => void updateRowStatus(row, k, e.target.value)}
                className="h-7 rounded border border-border bg-surface-card px-1 text-xs text-content-primary outline-none focus:border-primary disabled:opacity-50"
                title="Change status"
              >
                {!cur && <option value="">—</option>}
                {choices.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            );
          }
          if (fkTarget) {
            const label = resolveFkLabel(k, row[k], { selfResource: meta });
            if (label) return label;
          }
          // 2) Datetimes (esp. `indate`) render as dd-MM-yyyy. Tooltip
          //    still carries the full ISO so power users can copy it.
          if (isDateField(k, row[k])) {
            return <DateCell iso={row[k] as string} />;
          }
          return formatCell(row[k]);
        },
      };
    });
    // A resource is "document-like" (gets the Print / Download / Share menu)
    // when it carries a money total or a *Number document field.
    const isDocument =
      filtered.some((k) => ['grandTotal', 'totalAmount', 'amount', 'refundAmount'].includes(k)) ||
      filtered.some((k) => /number$/i.test(k));
    const actions: DataGridColumn<Record<string, unknown>> = {
      accessor: '__actions' as never,
      title: 'Actions',
      width: isDocument ? 150 : 110,
      render: (row: Record<string, unknown>) => (
        <div className="flex items-center gap-2">
          {isDocument && <DocActionsMenu row={row} resource={resource ?? meta!.name} />}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              // Edit always opens the full-page form now — the
              // modal-style edit was retired together with the
              // "every popup looks the same" complaint. Use the
              // row's id field for the route param.
              const rowId = row[meta!.idField] ?? row.id;
              if (rowId != null) {
                // Flat URL for transaction kinds, api-nested otherwise.
                const flatSlug = KIND_SLUG[resource];
                navigate(flatSlug ? `/panel/${flatSlug}/${rowId}` : `/panel/api/${group}/${resource}/${rowId}`);
              } else {
                // Fallback: no id available → keep the legacy modal
                // so the user can still see/touch the row.
                setEditing(row);
              }
            }}
            className="text-content-secondary hover:text-primary"
            title="Edit"
          >
            <Pencil size={14} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setConfirmDelete(row); }}
            className="text-content-secondary hover:text-danger"
            title="Delete"
          >
            <Trash2 size={14} />
          </button>
        </div>
      ),
    };
    return [selectionCol, ...dataCols, actions];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, meta, fkVersion, selectedIds]);

  async function onDelete() {
    if (!meta || !confirmDelete) return;
    // G60 / §35.3: the backend's DELETE controller binds only to the
    // numeric `id` (int) — passing the business `code` (string) routes
    // to a non-existent /{code} action and returns 404. Probe-verified
    // across master-data + sales + purchase + hrm + restaurant
    // (scripts/probe-delete-audit.mjs). Always prefer numeric `id` and
    // fall back to `code` only when no numeric id is present.
    const rawId = confirmDelete.id ?? confirmDelete[meta.idField];
    if (rawId == null) return;
    setDeleting(true);
    try {
      await apiClient.delete(`${meta.basePath}/${encodeURIComponent(String(rawId))}`);
      // Other resources may reference this row by FK — drop the cached
      // option list so a re-render fetches the fresh names.
      invalidateFkCache(meta.basePath);
      toast.success('Deleted', `${meta.name} ${rawId} removed`);
      setConfirmDelete(null);
      await load();
    } catch (e) {
      toast.error('Delete failed', e instanceof Error ? e.message : 'Failed');
    } finally {
      setDeleting(false);
    }
  }

  // Detect a "category" FK column on this resource (menuCategoryId,
  // categoryId, productCategoryId, …) to power the category filter.
  const categoryField = useMemo(() => {
    const keys = meta ? meta.fields.map((f) => f.name) : (rows[0] ? Object.keys(rows[0]) : []);
    return keys.find((k) => /category/i.test(k) && /id$/i.test(k) && !!lookupFkTarget(k, { selfResource: meta })) ?? null;
  }, [meta, rows]);
  useEffect(() => {
    setCategoryFilter(new Set());
    if (!categoryField) { setCategoryOpts([]); return; }
    let cancelled = false;
    void getFkOptions(categoryField, { selfResource: meta }).then((o) => { if (!cancelled) setCategoryOpts(o); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryField]);

  // Apply the category filter + toolbar search across every visible field.
  // Case-insensitive substring match — no escape syntax, just types.
  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    let list = rows;
    if (categoryField && categoryFilter.size > 0) {
      list = list.filter((r) => categoryFilter.has(String(r[categoryField] ?? '')));
    }
    if (!term) return list;
    return list.filter((r) =>
      Object.entries(r).some(([k, v]) => {
        if (HIDDEN_GRID_COLUMNS.has(k)) return false;
        return v != null && String(v).toLowerCase().includes(term);
      }),
    );
  }, [rows, searchTerm, categoryField, categoryFilter]);

  // Columns the spreadsheet edits. Hide tenantId / indate / id-of-this-
  // entity (read-only) so the operator only edits the human-relevant
  // fields. Same set Hide-On-Create uses in GenericCrudForm.
  const SHEET_HIDDEN = new Set([
    'tenantId', 'createdAt', 'updatedAt', 'createdBy', 'updatedBy',
    'deletedAt', 'deletedBy', 'indate',
  ]);
  const sheetFields = useMemo(() => {
    if (!meta) return [] as string[];
    return meta.fields
      .filter((f) => !f.isId && !SHEET_HIDDEN.has(f.name))
      .map((f) => f.name);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta]);

  // Reset draft when leaving spreadsheet mode or when rows reload.
  useEffect(() => {
    if (viewMode === 'sheet') {
      setSheetDraft(rows.map((r) => ({ ...r })));
      setSheetDirty(new Set());
    } else {
      setSheetDraft(null);
      setSheetDirty(new Set());
    }
  }, [viewMode, rows]);

  function onSheetCellChange(rowIdx: number, fieldName: string, raw: string) {
    setSheetDraft((cur) => {
      if (!cur) return cur;
      const next = [...cur];
      const before = next[rowIdx]?.[fieldName];
      // Coerce numeric fields back to number; leave strings alone.
      const fieldMeta = meta?.fields.find((f) => f.name === fieldName);
      const coerced: unknown =
        fieldMeta?.kind === 'number' ? (raw === '' ? null : Number(raw))
        : fieldMeta?.kind === 'boolean' ? raw === 'true' || raw === '1'
        : raw;
      next[rowIdx] = { ...(next[rowIdx] ?? {}), [fieldName]: coerced };
      if (String(before ?? '') !== String(coerced ?? '')) {
        setSheetDirty((d) => {
          const out = new Set(d);
          out.add(rowIdx);
          return out;
        });
      }
      return next;
    });
  }

  // ─── Import / Export / Template handlers ───────────────────────
  function rowKey(r: Record<string, unknown>): string {
    return String(r[meta!.idField] ?? r.id ?? '');
  }

  /** Export the current rows as XLSX, using the visible columns. */
  async function handleExport() {
    if (!meta) return;
    const visibleKeys = [...new Set(rows.flatMap((r) => Object.keys(r)))]
      .filter((k) => !HIDDEN_GRID_COLUMNS.has(k));
    await exportToExcel(
      rows,
      visibleKeys.map((k) => ({ key: k, header: humaniseHeader(k) })),
      `${meta.name}-export-${new Date().toISOString().slice(0, 10)}`,
    );
    toast.success('Exported', `Downloaded ${rows.length} ${meta.name} row(s)`);
  }

  /** Download an empty XLSX template with the field headers only. */
  async function handleDownloadTemplate() {
    if (!meta) return;
    const visible = meta.fields
      .filter((f) => !HIDDEN_ON_TEMPLATE.has(f.name))
      .map((f) => ({ key: f.name, header: humaniseHeader(f.name) }));
    await exportToExcel(
      [{}], // single empty row so users see the column structure
      visible,
      `${meta.name}-template`,
    );
    toast.success('Template ready', 'Open the file, fill in rows, then Import.');
  }

  /** Reads an XLSX/CSV and POSTs every row to the basePath. */
  async function handleImport(file: File) {
    if (!meta) return;
    setImporting(true);
    try {
      const XLSX = await import('xlsx');
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer);
      const firstSheet = wb.Sheets[wb.SheetNames[0]!]!;
      const parsed = XLSX.utils.sheet_to_json<Record<string, unknown>>(firstSheet);
      // Map back display headers → field names by humanising each
      // field name and comparing case-insensitively.
      const headerToField = new Map<string, string>();
      for (const f of meta.fields) {
        headerToField.set(humaniseHeader(f.name).toLowerCase(), f.name);
        headerToField.set(f.name.toLowerCase(), f.name);
      }
      // Only send columns that map to a real field; drop unknown headers
      // and service-managed columns (id / tenantId / audit) that otherwise
      // break model-binding with `invalid_body`.
      const fieldNames = new Set(meta.fields.map((f) => f.name));
      let ok = 0, fail = 0;
      let firstErr = '';
      for (const raw of parsed) {
        const payload: Record<string, unknown> = {};
        for (const [hdr, value] of Object.entries(raw)) {
          const fname = headerToField.get(String(hdr).toLowerCase()) ?? hdr;
          if (HIDDEN_ON_TEMPLATE.has(fname)) continue;
          if (!fieldNames.has(fname)) continue;
          if (value === '' || value == null) continue;
          payload[fname] = value;
        }
        // `status` is the numeric enum (0/1/2). An Excel "Active"/"Passive"
        // label or blank would 400 — coerce it to a number.
        if ('status' in payload) payload.status = isActiveFromStatus(payload.status);
        try {
          await apiClient.post(meta.basePath, payload);
          ok += 1;
        } catch (e) {
          fail += 1;
          if (!firstErr) {
            const ax = e as { response?: { data?: { message?: string; error?: string } }; message?: string };
            firstErr = ax?.response?.data?.message ?? ax?.response?.data?.error ?? ax?.message ?? 'Unknown error';
          }
        }
      }
      if (fail === 0) toast.success('Imported', `${ok} row(s) created.`);
      else toast.error('Partial import', `${ok} ok · ${fail} failed${firstErr ? ` — ${firstErr.slice(0, 160)}` : ''}`);
      await load();
    } catch (e) {
      toast.error('Import failed', e instanceof Error ? e.message : 'Bad file');
    } finally {
      setImporting(false);
      if (importInputRef.current) importInputRef.current.value = '';
    }
  }

  async function saveSheet() {
    if (!meta || !sheetDraft || sheetDirty.size === 0) return;
    setSheetSaving(true);
    const dirtyIndices = [...sheetDirty];
    let succeeded = 0;
    let failed = 0;
    for (const i of dirtyIndices) {
      const row = sheetDraft[i];
      if (!row) continue;
      try {
        await apiClient.put(meta.basePath, row);
        succeeded++;
      } catch {
        failed++;
      }
    }
    setSheetSaving(false);
    invalidateFkCache(meta.basePath);
    if (failed === 0) {
      toast.success('Saved', `${succeeded} row(s) updated`);
    } else {
      toast.error(
        failed === dirtyIndices.length ? 'Save failed' : 'Partial save',
        `${succeeded} succeeded · ${failed} failed`,
      );
    }
    setSheetDirty(new Set());
    await load();
  }

  if (!meta) {
    return (
      <div className="-m-6 p-[3px]">
        <Alert variant="warning">
          Resource not in registry: {group}/{resource}
        </Alert>
      </div>
    );
  }

  // Hidden file input wired for the +Add dropdown's "Import" action.
  const importInput = (
    <input
      ref={importInputRef}
      type="file"
      accept=".xlsx,.xls,.csv"
      hidden
      onChange={(e) => {
        const f = e.target.files?.[0];
        if (f) void handleImport(f);
      }}
    />
  );

  // The single slim toolbar above the grid. Replaces the old big
  // outer card with breadcrumb + path + Grid/Sheet/Reload/+New.
  // Layout (left → right):
  //   • Resource label (humanised, no `master-data · GET /api/...` clutter)
  //   • Collapsible search (icon → textbox + X)
  //   • Reload
  //   • +Add dropdown { New, Import Excel, Export Excel, Download Template }
  //   • Grid / Sheet toggle (inline)
  const onAddNew = () => {
    const flatSlug = KIND_SLUG[resource];
    navigate(flatSlug ? `/panel/${flatSlug}/new` : `/panel/api/${group}/${resource}/new`);
  };
  const gridToolbar = (
    <div className="relative flex items-center gap-2 border-b border-border bg-surface-card px-3 py-2">
      {/* LEFT — back arrow + title. When search expands it overlays
          this whole area (absolute), so the right-side buttons never
          reflow to a new line on narrow viewports. */}
      <div className={`flex min-w-0 items-center gap-2 ${searchOpen ? 'invisible' : ''}`}>
        <Link
          to={`/panel/api/${group}`}
          className="grid h-8 w-8 place-items-center rounded-md text-content-secondary hover:bg-surface-card-hover hover:text-content-primary"
          title="Back to group"
          aria-label="Back to group"
        >
          <ChevronLeft size={16} />
        </Link>
        <h2 className="truncate text-sm font-semibold text-content-primary">
          {humaniseHeader(meta.name)}
        </h2>
      </div>
      <div className="flex-1" />

      {/* Expanded search overlay — positioned absolute over the title
          area so the right-side buttons keep their place. */}
      {searchOpen && (
        <div className="absolute inset-y-1 left-2 z-10 flex items-center gap-1 rounded-md border border-primary bg-surface-card pl-2 pr-1 shadow-sm sm:right-auto sm:w-[60%]">
          <Search className="h-3.5 w-3.5 text-content-muted" />
          <input
            autoFocus
            type="text"
            placeholder={`Search ${humaniseHeader(meta.name).toLowerCase()}…`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-8 flex-1 bg-transparent text-xs text-content-primary outline-none"
          />
          <button
            type="button"
            className="grid h-7 w-7 place-items-center rounded text-content-muted hover:bg-surface-card-hover hover:text-content-primary"
            onClick={() => { setSearchOpen(false); setSearchTerm(''); }}
            aria-label="Close search"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* RIGHT — fixed in place regardless of search state */}
      {categoryField && categoryOpts.length > 0 && (
        <CategoryMultiSelect options={categoryOpts} selected={categoryFilter} onChange={setCategoryFilter} />
      )}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setSearchOpen((o) => !o)}
        aria-label={searchOpen ? 'Close search' : 'Search'}
        title={searchOpen ? 'Close search' : 'Search rows'}
      >
        <Search className="h-3.5 w-3.5" />
      </Button>

      <Button variant="outline" size="sm" onClick={load} disabled={loading}>
        <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Reload
      </Button>

      {/* User registry has no role picker in its form (roles live in the
          separate UserRole join table) — surface a quick link to the
          dedicated role-assignment page. */}
      {group === 'identity' && resource === 'User' && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/panel/security/user-roles')}
          title="Assign roles to users"
        >
          <UserCog className="h-3.5 w-3.5" /> Assign Roles
        </Button>
      )}

      {viewMode === 'sheet' && (
        <Button
          size="sm"
          onClick={() => void saveSheet()}
          disabled={sheetDirty.size === 0 || sheetSaving}
          title={sheetDirty.size === 0 ? 'No edits to save' : `Save ${sheetDirty.size} dirty row(s)`}
        >
          {sheetSaving ? <Spinner /> : <Save className="h-3.5 w-3.5" />}
          Save{sheetDirty.size > 0 ? ` (${sheetDirty.size})` : ''}
        </Button>
      )}

      <AddDropdownButton
        label="Add"
        onNew={onAddNew}
        onImport={() => importInputRef.current?.click()}
        onExport={() => void handleExport()}
        onTemplate={() => void handleDownloadTemplate()}
        importing={importing}
        canExport={!loading && rows.length > 0}
      />

      {/* Grid / Sheet view toggle — kept inline next to the +Add. */}
      <div className="inline-flex items-center rounded-md border border-border bg-surface-card p-0.5 text-xs" title="Toggle view">
        <button
          type="button"
          onClick={() => setViewMode('grid')}
          className={`flex items-center gap-1 rounded px-2 py-1 ${
            viewMode === 'grid' ? 'bg-primary text-white' : 'text-content-secondary hover:text-content-primary'
          }`}
          aria-pressed={viewMode === 'grid'}
          aria-label="Grid view"
        >
          <LayoutGrid className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => setViewMode('sheet')}
          className={`flex items-center gap-1 rounded px-2 py-1 ${
            viewMode === 'sheet' ? 'bg-primary text-white' : 'text-content-secondary hover:text-content-primary'
          }`}
          aria-pressed={viewMode === 'sheet'}
          aria-label="Spreadsheet view"
        >
          <Table2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="-m-6 flex h-[calc(100vh-3.5rem)] flex-col p-[3px]">
      {importInput}
      {err && <Alert variant="danger" className="mb-2">{err}</Alert>}

      {/* One unified card: slim toolbar on top, grid/sheet body below. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-border bg-surface-card">
        {gridToolbar}
        <div className="flex-1 overflow-auto">
          {loading && rows.length === 0 ? (
            <div className="flex items-center gap-2 p-6 text-sm text-content-muted">
              <Spinner /> Loading from /api{meta.basePath}…
            </div>
          ) : rows.length === 0 ? (
            <div className="p-6 text-sm text-content-muted">
              No rows returned. The endpoint may be empty for this tenant, or it may require
              additional filters.
            </div>
          ) : viewMode === 'sheet' && sheetDraft ? (
            <div className="p-3">
              <div className="mb-2 flex items-center gap-2 text-[11px] text-content-muted">
                <Grid3X3 className="h-3 w-3" />
                Editing inline. Press <kbd className="rounded border border-border bg-surface-card px-1">Enter</kbd>
                to commit a cell. Dirty rows are PUT back to{' '}
                <code>{meta.basePath}</code> when you click Save.
              </div>
              <Spreadsheet
                columnLabels={sheetFields.map(humaniseHeader)}
                rowLabels={sheetDraft.map((r, i) => {
                  const id = r[meta.idField] ?? r.id;
                  return String(id != null ? id : i + 1);
                })}
                data={sheetDraft.map((row) =>
                  sheetFields.map((f) => ({ value: cellString(row[f]) })),
                )}
                onChange={(next) => {
                  next.forEach((cells, rowIdx) => {
                    cells?.forEach((cell, colIdx) => {
                      const fname = sheetFields[colIdx];
                      if (!fname) return;
                      const raw = (cell?.value ?? '') as string;
                      const prev = cellString(sheetDraft?.[rowIdx]?.[fname]);
                      if (raw !== prev) onSheetCellChange(rowIdx, fname, raw);
                    });
                  });
                }}
              />
            </div>
          ) : (
            <DataGrid<Record<string, unknown>>
              data={filteredRows}
              columns={columns}
              rowId={meta.idField as keyof Record<string, unknown> & string}
              // The toolbar above already has a collapsible search;
              // turn off DataGrid's built-in floating one so we don't
              // show two search inputs.
              searchable={false}
            />
          )}
        </div>
      </div>

      <GenericCrudForm
        meta={meta}
        row={editing}
        open={editing !== undefined}
        onClose={() => setEditing(undefined)}
        onSaved={() => {
          // Reload this resource's rows AND drop the FK cache for it —
          // any other page in this SPA session that has cached the FK
          // options for this entity (e.g. an Employee form whose
          // department dropdown was populated from this list) would
          // otherwise miss the newly-created row.
          invalidateFkCache(meta.basePath);
          void load();
        }}
      />

      {imagePreview && (
        <ImagePreviewModal src={imagePreview} onClose={() => setImagePreview(null)} />
      )}

      <ConfirmDialog
        isOpen={!!confirmDelete}
        title={`Delete this ${meta.name}?`}
        message={
          confirmDelete
            ? `${String((confirmDelete[meta.idField] ?? '') || '')} — ${
                String(confirmDelete['name'] ?? confirmDelete['code'] ?? '')
              }`
            : ''
        }
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        onClose={() => setConfirmDelete(null)}
        onConfirm={onDelete}
        variant="danger"
      />
    </div>
  );
}

/**
 * Columns the registry-driven grid hides by default. Tenant id is
 * implicit (every row is scoped to the current tenant) and the audit
 * trail columns (updated / created / deleted by/at) just crowd out
 * the business fields. Power users can still see them by inspecting
 * the JSON view or opening the edit modal.
 */
/**
 * String workflow-status columns that render as an inline dropdown in the
 * grid so an admin can advance the stage without opening the editor
 * (e.g. a restaurant order Placed→Cooking→Ready→Delivered). Keyed by the
 * column name; value is the ordered stage list.
 */
const WORKFLOW_STATUS_OPTIONS: Record<string, string[]> = {
  orderStatus: ['Placed', 'Accepted', 'Cooking', 'Ready', 'Served', 'Delivered', 'Billed', 'Cancelled'],
  challanStatus: ['Pending', 'Dispatched', 'In Transit', 'Delivered', 'Cancelled'],
  invoiceStatus: ['Draft', 'Posted', 'Paid', 'Cancelled'],
  paymentStatus: ['Unpaid', 'Partial', 'Paid', 'Refunded'],
  refundStatus: ['Pending', 'Approved', 'Refunded', 'Rejected'],
  opportunityStatus: ['Open', 'Won', 'Lost'],
  quotationStatus: ['Draft', 'Sent', 'Accepted', 'Rejected', 'Expired'],
  inspectionStatus: ['Pending', 'Passed', 'Failed'],
};

const HIDDEN_GRID_COLUMNS = new Set<string>([
  'id',           // surrogate PK — opaque, not user-meaningful
  'tenantId',
  'updatedAt', 'updatedBy',
  'createdBy',
  'deletedAt', 'deletedBy',
  // `remarks` is an optional free-text note — hidden from every grid
  // (and every entry form, see HIDDEN_ALWAYS) per request.
  'remarks',
  // `description` often holds a line-item JSON envelope (transaction shells)
  // or long free text — removed from every grid per request.
  'description',
]);

/**
 * High-value business columns that must ALWAYS appear in the grid even if
 * they sort past the column cap — money + workflow-status fields like
 * grandTotal and paymentStatus (e.g. PurchaseInvoice has ~18 fields, so
 * these would otherwise be cut off).
 */
const IMPORTANT_GRID_COLUMNS = new Set<string>([
  'grandtotal', 'totalamount', 'amount', 'refundamount', 'paidamount', 'balance',
  'subtotal', 'taxamount',
  'paymentstatus', 'invoicestatus', 'orderstatus', 'approvalstatus', 'refundstatus',
  'invoicenumber', 'invoicedate', 'suppliername', 'customername',
  // Buyer's PO reference + common doc numbers — always surface so they don't
  // sort past the column cap on wide schemas (reported: Buyer P.O. No missing
  // from the Sales Order grid).
  'buyerponumber', 'ordernumber', 'returnnumber', 'challannumber',
]);

/**
 * Columns we DON'T offer in the import template — the backend
 * generates them (id, tenantId, audit timestamps) so the user
 * shouldn't be asked to provide them.
 */
const HIDDEN_ON_TEMPLATE = new Set<string>([
  'id', 'tenantId', 'indate', 'createdAt', 'createdBy',
  'updatedAt', 'updatedBy', 'deletedAt', 'deletedBy',
]);

function isTruthy(v: unknown): boolean {
  if (v == null) return false;
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    return s === '1' || s === 'true' || s === 'yes' || s === 'active' || s === 'y';
  }
  return false;
}

function isDateField(key: string, value: unknown): boolean {
  // Heuristic: any field whose name implies a timestamp OR whose
  // value looks like an ISO string. The registry's `kind === datetime`
  // metadata would be cleaner but `formatCell` doesn't have field
  // meta in scope here; key-based heuristics are good enough.
  if (typeof value !== 'string') return false;
  if (!/^\d{4}-\d{2}-\d{2}/.test(value)) return false;
  return (
    /^(indate|date|expiresAt|issuedAt|placedAt|servedAt|billedAt|paidAt|cookingStartedAt|readyAt|acceptedAt|dueDate|deliveryDate|orderDate|invoiceDate|paymentDate|joinDate|exitDate|dateOfJoining|dateOfLeaving|birthDate|lastLogin)/i.test(key) ||
    /At$|Date$/.test(key)
  );
}

function formatDDMMYYYY(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
}

/**
 * +Add split-button. Primary click = create new. The chevron opens a
 * popover with three secondary actions: Import Excel, Export Excel,
 * Download Template. Clicks outside the popover close it.
 */
function AddDropdownButton({
  label, onNew, onImport, onExport, onTemplate, importing, canExport,
}: {
  label: string;
  onNew: () => void;
  onImport: () => void;
  onExport: () => void;
  onTemplate: () => void;
  importing: boolean;
  canExport: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        type="button"
        onClick={onNew}
        className="inline-flex h-8 items-center gap-1 rounded-l-md bg-primary px-3 text-xs font-medium text-white hover:bg-primary-hover"
      >
        <Plus className="h-3.5 w-3.5" />
        {label}
      </button>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex h-8 items-center justify-center rounded-r-md border-l border-white/20 bg-primary px-2 text-white hover:bg-primary-hover"
        aria-label="More actions"
      >
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-40 mt-1 w-52 overflow-hidden rounded-md border border-border bg-surface-card shadow-lg">
          <button
            type="button"
            onClick={() => { setOpen(false); onImport(); }}
            disabled={importing}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-content-primary hover:bg-surface-card-hover disabled:opacity-50"
          >
            <Upload className="h-3.5 w-3.5" /> {importing ? 'Importing…' : 'Import Excel'}
          </button>
          <button
            type="button"
            onClick={() => { setOpen(false); onExport(); }}
            disabled={!canExport}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-content-primary hover:bg-surface-card-hover disabled:opacity-50"
          >
            <Download className="h-3.5 w-3.5" /> Export Excel
          </button>
          <button
            type="button"
            onClick={() => { setOpen(false); onTemplate(); }}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-content-primary hover:bg-surface-card-hover"
          >
            <FileDown className="h-3.5 w-3.5" /> Download Template
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Resolve a tenant-relative `/uploads/...` URL to absolute by
 * prepending the API host. Mirrors the helper in GenericCrudForm —
 * inlined here so the grid doesn't need to import it.
 */
function resolveImageSrc(src: string): string {
  if (!src) return src;
  if (src.startsWith('data:') || /^https?:\/\//i.test(src)) return src;
  if (!src.startsWith('/')) return src;
  const apiBase = String(apiClient.defaults.baseURL ?? '');
  if (!apiBase || apiBase.startsWith('/')) return src;
  return apiBase.replace(/\/api\/?$/, '') + src;
}

function ImageThumbnail({ src, onOpen }: { src: string; onOpen: () => void }) {
  const resolved = resolveImageSrc(src);
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onOpen(); }}
      className="group relative h-10 w-10 overflow-hidden rounded border border-border bg-surface-card"
      title="Click to enlarge"
    >
      <AuthImg
        src={resolved}
        className="h-full w-full object-cover transition-transform group-hover:scale-110"
      />
      <ZoomIn className="pointer-events-none absolute right-0.5 top-0.5 h-3 w-3 text-white opacity-0 drop-shadow group-hover:opacity-100" />
    </button>
  );
}

function ImagePreviewModal({ src, onClose }: { src: string; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6"
      onClick={onClose}
    >
      <div
        className="relative max-h-full max-w-4xl overflow-auto rounded-lg bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-2 top-2 grid h-8 w-8 place-items-center rounded-full bg-black/40 text-white hover:bg-black/60"
          aria-label="Close preview"
        >
          <X className="h-4 w-4" />
        </button>
        <AuthImg src={resolveImageSrc(src)} className="block max-h-[80vh] max-w-full" />
      </div>
    </div>
  );
}

function DateCell({ iso }: { iso: string }) {
  return (
    <span title={iso} className="whitespace-nowrap">
      {formatDDMMYYYY(iso)}
    </span>
  );
}

function StatusPill({ on, label }: { on: boolean; label: string }) {
  return (
    <span
      className={
        on
          ? 'inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-700 dark:bg-green-900/30 dark:text-green-300'
          : 'inline-flex items-center rounded-full bg-gray-200 px-2 py-0.5 text-[11px] font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300'
      }
    >
      {label}
    </span>
  );
}

function formatCell(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'object') return JSON.stringify(v).slice(0, 60);
  return String(v);
}

/**
 * Turn a camelCase field name into a human-readable header.
 * "customerName" → "Customer Name", "departmentId" → "Department Id".
 * Used for the registry-driven grid headers so the operator sees
 * proper labels instead of raw API field names.
 */
/** Coerce any cell value into the plain-string shape react-spreadsheet wants. */
function cellString(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function humaniseHeader(s: string): string {
  if (!s) return '';
  const spaced = s
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

const escHtml = (s: unknown) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));
const docNumberOf = (row: Record<string, unknown>) =>
  String(row.invoiceNumber ?? row.paymentNumber ?? row.returnNumber ?? row.requisitionNumber ?? (row as { creditNoteNumber?: unknown }).creditNoteNumber ?? row.code ?? row.id ?? '');
const docTotalOf = (row: Record<string, unknown>) =>
  Number(row.grandTotal ?? row.totalAmount ?? row.amount ?? row.refundAmount ?? 0);

/** Build a generic printable/downloadable HTML doc from any row. */
function buildDocHtml(row: Record<string, unknown>, resource: string): string {
  const title = `${humaniseHeader(resource)} ${docNumberOf(row)}`.trim();
  const skip = new Set<string>([...HIDDEN_GRID_COLUMNS, '__select', '__actions', 'code', 'name', 'status', 'indate', 'createdAt']);
  const fieldRows = Object.entries(row)
    .filter(([k, v]) => !skip.has(k) && v != null && typeof v !== 'object' && String(v) !== '')
    .map(([k, v]) => `<tr><td class="k">${escHtml(humaniseHeader(k.replace(/Id$/, '')))}</td><td>${escHtml(v)}</td></tr>`)
    .join('');
  let linesHtml = '';
  try {
    const d = String(row.description ?? '');
    if (d.startsWith('{')) {
      const env = JSON.parse(d) as { lines?: Array<Record<string, unknown>>; items?: Array<Record<string, unknown>> };
      const lines = env.lines?.length ? env.lines : env.items;
      if (Array.isArray(lines) && lines.length) {
        linesHtml = `<h3>Items</h3><table class="lines"><thead><tr><th>Item</th><th>Qty</th><th>Rate</th><th>Amount</th></tr></thead><tbody>${lines.map((l) => {
          const qty = Number(l.quantity ?? 0); const rate = Number(l.unitPrice ?? 0);
          return `<tr><td>${escHtml(l.name ?? l.productName ?? '')}</td><td>${escHtml(qty)}</td><td>${escHtml(rate)}</td><td>${escHtml(Number(l.lineTotal ?? qty * rate))}</td></tr>`;
        }).join('')}</tbody></table>`;
      }
    }
  } catch { /* no envelope */ }
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escHtml(title)}</title><style>
    body{font-family:system-ui,Arial,sans-serif;padding:24px;color:#111}
    h1{font-size:20px;margin:0 0 12px}h3{margin:18px 0 6px}
    table{border-collapse:collapse;width:100%;font-size:13px;margin-bottom:8px}
    td,th{border:1px solid #ddd;padding:6px 8px;text-align:left}
    .k{width:220px;color:#555;background:#fafafa}.lines th{background:#f3f4f6}
  </style></head><body><h1>${escHtml(title)}</h1><table>${fieldRows}</table>${linesHtml}</body></html>`;
}

/**
 * Per-row document actions for invoice/payment/return/etc. grids:
 * Print PDF (browser print → save as PDF), Download (HTML file), and
 * Share on WhatsApp (wa.me text summary).
 */
function DocActionsMenu({ row, resource }: { row: Record<string, unknown>; resource: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // Portal + flip-up positioning so the menu can't be clipped by the table /
  // viewport bottom on low rows (#7/#9/#12 "Print dropdown goes below screen").
  const [pos, setPos] = useState<{ right: number; top?: number; bottom?: number } | null>(null);
  useEffect(() => {
    if (!open) { setPos(null); return; }
    const place = () => {
      const r = ref.current?.getBoundingClientRect();
      if (!r) return;
      const below = window.innerHeight - r.bottom;
      const openUp = below < 150 && r.top > below;
      setPos({
        right: Math.max(8, window.innerWidth - r.right),
        ...(openUp ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }),
      });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const title = `${humaniseHeader(resource)} ${docNumberOf(row)}`.trim();
  const printPdf = () => {
    // Prefer the proper branded template (full line items + party + letterhead)
    // for document resources; fall back to the generic key-value print.
    void printRegistryDoc(resource, row).then((handled) => {
      if (handled) return;
      const w = window.open('', '_blank', 'width=820,height=920');
      if (!w) return;
      w.document.write(buildDocHtml(row, resource));
      w.document.close();
      w.onload = () => w.print();
    }).catch(() => { /* fall through to generic on error */
      const w = window.open('', '_blank', 'width=820,height=920');
      if (!w) return;
      w.document.write(buildDocHtml(row, resource));
      w.document.close();
      w.onload = () => w.print();
    });
  };
  const download = () => {
    const blob = new Blob([buildDocHtml(row, resource)], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${title || 'document'}.html`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const shareWhatsApp = () => {
    const party = String(row.supplierName ?? row.customerName ?? '');
    const total = docTotalOf(row);
    const text = [title, party && `Party: ${party}`, total && `Total: ₹${total.toLocaleString('en-IN')}`].filter(Boolean).join('\n');
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const Item = ({ icon: Icon, label, onClick }: { icon: typeof Printer; label: string; onClick: () => void }) => (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); setOpen(false); }}
      className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs text-content-primary hover:bg-surface-card-hover"
    >
      <Icon size={13} /> {label}
    </button>
  );

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        className="inline-flex items-center text-content-secondary hover:text-primary"
        title="Print / Download / Share"
        aria-label="Print, download or share"
      >
        <Printer size={14} />
        <ChevronDown size={11} />
      </button>
      {open && pos && createPortal(
        <div
          ref={menuRef}
          style={{ position: 'fixed', right: pos.right, top: pos.top, bottom: pos.bottom, zIndex: 100 }}
          className="w-44 rounded-md border border-border bg-surface-card p-1 shadow-xl"
        >
          <Item icon={Printer} label="Print PDF" onClick={printPdf} />
          <Item icon={Download} label="Download" onClick={download} />
          <Item icon={Share2} label="Share WhatsApp" onClick={shareWhatsApp} />
        </div>,
        document.body,
      )}
    </div>
  );
}

/**
 * Checkbox multi-select category filter (e.g. MenuItem ▸ filter by several
 * categories at once). Empty selection = show all. Closes on outside click.
 */
function CategoryMultiSelect({ options, selected, onChange }: {
  options: FkOption[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const count = selected.size;
  const toggle = (id: string) => { const n = new Set(selected); if (n.has(id)) n.delete(id); else n.add(id); onChange(n); };
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex h-8 items-center gap-1 rounded-md border border-border bg-surface-card px-2 text-xs text-content-primary hover:border-primary"
        title="Filter by category"
      >
        {count ? `${count} categor${count > 1 ? 'ies' : 'y'}` : 'All categories'}
        <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 max-h-72 w-56 overflow-y-auto rounded-md border border-border bg-surface-card p-1 shadow-lg">
          <div className="flex items-center justify-between px-2 py-1">
            <span className="text-[11px] font-medium text-content-muted">Filter by category</span>
            {count > 0 && (
              <button type="button" onClick={() => onChange(new Set())} className="text-[11px] text-primary hover:underline">Clear</button>
            )}
          </div>
          {options.map((o) => {
            const id = String(o.id);
            return (
              <label key={id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs hover:bg-surface-card-hover">
                <input type="checkbox" checked={selected.has(id)} onChange={() => toggle(id)} className="accent-primary" />
                <span className="truncate">{o.label}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
